import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useProductCategories } from '@/hooks/useProductCategories';
import { Loader2, Save, Plus, Edit2, BarChart } from 'lucide-react';

export function AISystemPromptManager() {
  const { toast } = useToast();
  const { categories: productCategories, loading: loadingCategories } = useProductCategories();
  const [prompts, setPrompts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    product_category_id: null as string | null,
    prompt_name: '',
    prompt_content: '',
    model_id: '',
  });

  useEffect(() => {
    fetchPrompts();
  }, []);

  const fetchPrompts = async () => {
    try {
      const { data, error } = await supabase
        .from('ai_system_prompts')
        .select('*, product_categories(name)')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPrompts(data || []);
    } catch (error) {
      console.error('Error fetching prompts:', error);
      toast({
        title: 'Error',
        description: 'Failed to load system prompts',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.prompt_name.trim() || !formData.prompt_content.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Prompt name and content are required',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        const { error } = await supabase
          .from('ai_system_prompts')
          .update({
            prompt_name: formData.prompt_name,
            prompt_content: formData.prompt_content,
            model_id: formData.model_id.trim() || null,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editingId);

        if (error) throw error;
        toast({ title: 'Success', description: 'System prompt updated' });
      } else {
        const { error } = await supabase
          .from('ai_system_prompts')
          .insert({
            product_category_id: formData.product_category_id,
            prompt_name: formData.prompt_name,
            prompt_content: formData.prompt_content,
            model_id: formData.model_id.trim() || null,
          });

        if (error) throw error;
        toast({ title: 'Success', description: 'System prompt created' });
      }

      setFormData({ product_category_id: null, prompt_name: '', prompt_content: '', model_id: '' });
      setEditingId(null);
      fetchPrompts();
    } catch (error) {
      console.error('Error saving prompt:', error);
      toast({
        title: 'Error',
        description: 'Failed to save system prompt',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (prompt: any) => {
    setEditingId(prompt.id);
    setFormData({
      product_category_id: prompt.product_category_id,
      prompt_name: prompt.prompt_name,
      prompt_content: prompt.prompt_content,
      model_id: prompt.model_id || '',
    });
  };

  const handleCancel = () => {
    setEditingId(null);
    setFormData({ product_category_id: null, prompt_name: '', prompt_content: '', model_id: '' });
  };

  if (loading || loadingCategories) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Spec Management Navigation */}
      <div className="flex gap-2 border-b pb-4">
        <Button asChild variant="ghost" size="sm">
          <Link to="/admin/ai-usage">
            <BarChart className="w-4 h-4 mr-2" />
            AI Usage & Analytics
          </Link>
        </Button>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle>{editingId ? 'Edit System Prompt' : 'Create New System Prompt'}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="category">Product Category</Label>
            <Select
              value={formData.product_category_id || 'default'}
              onValueChange={(v) => setFormData({ ...formData, product_category_id: v === 'default' ? null : v })}
            >
              <SelectTrigger id="category">
                <SelectValue placeholder="Select category or default" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Default (All Categories)</SelectItem>
                {productCategories.filter(c => c.is_active).map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Category-specific prompts override the default prompt
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">Prompt Name</Label>
            <Input
              id="name"
              value={formData.prompt_name}
              onChange={(e) => setFormData({ ...formData, prompt_name: e.target.value })}
              placeholder="e.g., Packaging Specification Assistant"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="content">Prompt Content</Label>
            <Textarea
              id="content"
              value={formData.prompt_content}
              onChange={(e) => setFormData({ ...formData, prompt_content: e.target.value })}
              placeholder="Enter the system prompt that will guide the AI assistant..."
              rows={12}
              className="font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground">
              The AI will receive this prompt along with the specification field definitions
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="model">Model Override (Optional)</Label>
            <Input
              id="model"
              value={formData.model_id}
              onChange={(e) => setFormData({ ...formData, model_id: e.target.value })}
              placeholder="e.g., claude-sonnet-4-5"
            />
            <p className="text-xs text-muted-foreground">
              Leave blank to use system default (claude-sonnet-4-5). The system will automatically fall back to an available model if the specified model is unavailable.
            </p>
          </div>

          <div className="flex justify-end gap-2">
            {editingId && (
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
            )}
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  {editingId ? 'Update Prompt' : 'Create Prompt'}
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Existing System Prompts</h3>
        {prompts.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              No system prompts configured yet
            </CardContent>
          </Card>
        ) : (
          prompts.map((prompt) => (
            <Card key={prompt.id}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base">{prompt.prompt_name}</CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      {prompt.product_category_id 
                        ? `Category: ${(prompt as any).product_categories?.name}` 
                        : 'Default for all categories'}
                      {prompt.model_id && ` • Model: ${prompt.model_id}`}
                    </p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => handleEdit(prompt)}>
                    <Edit2 className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="bg-muted p-3 rounded-md">
                  <pre className="text-xs whitespace-pre-wrap font-mono">
                    {prompt.prompt_content.slice(0, 300)}
                    {prompt.prompt_content.length > 300 && '...'}
                  </pre>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
